import { Link } from "react-router-dom";
import { ArrowRight, MapPin } from "lucide-react";
import { Seo } from "@/components/site/Seo";
import { Hero } from "@/components/site/Hero";
import { PageShell } from "@/components/site/PageShell";
import { CtaBand, SectionHeading } from "@/components/sections/Sections";
import { KOELN, UMLAND } from "@/data/areas";
import { breadcrumbLd, businessLd } from "@/lib/jsonld";

const crumbs = [{ label: "Einsatzgebiet", to: "/einsatzgebiet" }];

const Group = ({ title, intro, items }) => (
  <section>
    <SectionHeading eyebrow="Einsatzgebiet" title={title} intro={intro} />
    <ul className="grid gap-3 sm:grid-cols-2">
      {items.map((l) => (
        <li key={l.slug}>
          <Link to={`/${l.slug}`} data-testid={`area-card-${l.slug}`}
            className="group flex h-full gap-4 rounded-2xl border border-slate-200 p-5 transition-[border-color,box-shadow,transform] hover:-translate-y-0.5 hover:border-brand-blue/40 hover:shadow-soft">
            <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-brand-yellow text-brand-anthracite"><MapPin className="h-5 w-5" /></span>
            <span className="flex-1">
              <span className="block font-bold text-brand-anthracite group-hover:text-brand-blue">Kfz-Gutachter {l.label}</span>
              <span className="mt-1 block text-sm leading-relaxed">{l.subtitle}</span>
            </span>
            <ArrowRight className="mt-1 h-4 w-4 shrink-0 text-brand-blue transition-transform group-hover:translate-x-1" />
          </Link>
        </li>
      ))}
    </ul>
  </section>
);

export default function AreasPage() {
  return (
    <>
      <Seo
        title="Einsatzgebiet: Kfz-Gutachter Köln & Umgebung | Marcone"
        description="Kfz-Gutachter für Köln und Umgebung: Nippes, Ehrenfeld, Innenstadt, Leverkusen, Pulheim, Hürth und mehr. Meist innerhalb von 24 h vor Ort. Jetzt anfragen!"
        path="/einsatzgebiet"
        jsonLd={[businessLd(), breadcrumbLd(crumbs)]}
      />
      <PageShell
        hero={
          <Hero
            image="real-marcone-auto"
            imagePosition="center 20%"
            imageAlt="Kfz-Sachverständiger Francesco Marcone bei der Schadenaufnahme im Kölner Einsatzgebiet"
            eyebrow="Einsatzgebiet"
            title="Kfz-Gutachter in Köln und Umgebung"
            subtitle="Von unserem Büro in Köln-Nippes sind wir schnell in allen Kölner Stadtteilen und im Umland – meist innerhalb von 24 Stunden."
            breadcrumbs={crumbs}
          />
        }
      >
        <div className="space-y-16" data-testid="areas-page">
          <p className="text-base leading-relaxed">
            Ob Sie in der Kölner Innenstadt, im Kölner Norden oder in einer Nachbarstadt wohnen: Wir kommen zu Ihnen nach Hause, an den Arbeitsplatz
            oder in die Werkstatt Ihrer Wahl. Wählen Sie Ihren Ort, um mehr über Anfahrt, typische Einsatzorte und häufige Fragen zu erfahren.
          </p>
          <Group title="Kölner Stadtteile" intro="Unser Büro liegt in Nippes – entsprechend schnell sind wir im gesamten Stadtgebiet." items={KOELN} />
          <Group title="Umland" intro="Auch in den Nachbarstädten rund um Köln sind wir regelmäßig im Einsatz." items={UMLAND} />
          <CtaBand prefix="areas-cta" />
        </div>
      </PageShell>
    </>
  );
}
